'use client'

import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { X, ExternalLink, Bike } from 'lucide-react'

type Props = {
  open: boolean
  onClose: () => void
  /** Color de marca del merchant. Se pasa explícito porque el modal se monta vía
   *  portal fuera del wrapper MicrositeTheme (donde vive var(--ms-accent)). */
  accentColor?: string
  doordashUrl?: string
  uberEatsUrl?: string
  grubhubUrl?: string
}

/**
 * Popup de delivery: muestra accesos directos a las apps de terceros que el
 * merchant tenga configuradas (DoorDash / Uber Eats / Grubhub). Cada plataforma
 * abre en una pestaña nueva (página de cliente => target=_blank).
 *
 * Se monta vía portal a document.body para escapar del stacking context del nav
 * fijo. Panel claro a propósito (look de modal universal), botones con color de
 * marca como en DeliverySection.
 */
export function DeliveryModal({ open, onClose, accentColor, doordashUrl, uberEatsUrl, grubhubUrl }: Props) {
  const t = useTranslations('public.microsite')

  // Cerrar con Escape + bloquear el scroll del fondo mientras está abierto.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  // El portal necesita document; en SSR no se renderiza nada (el footprint en el
  // árbol es vacío en ambos lados => sin hydration mismatch).
  if (typeof document === 'undefined') return null

  const providers = [
    { key: 'doordash', label: 'DoorDash', color: '#EB1700', url: doordashUrl },
    { key: 'ubereats', label: 'Uber Eats', color: '#000000', url: uberEatsUrl },
    { key: 'grubhub', label: 'Grubhub', color: '#F63440', url: grubhubUrl },
  ].filter((p) => !!p.url && p.url.trim().length > 0)

  return createPortal(
    <AnimatePresence>
      {open && providers.length > 0 && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={t('deliveryTitle')}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-white p-6 sm:p-7 shadow-2xl"
            style={{ color: '#171717' }}
            initial={{ y: 28, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 28, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label={t('deliveryClose')}
              className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-full text-gray-500 hover:bg-black/5 transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div
              className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-4"
              style={{ background: accentColor ?? '#ea580c' }}
            >
              <Bike className="w-5 h-5 text-white" />
            </div>

            <h2 className="text-xl font-bold pr-8">{t('deliveryTitle')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('deliverySubtitle')}</p>

            <div className="mt-6 flex flex-col gap-3">
              {providers.map((p) => (
                <a
                  key={p.key}
                  href={p.url!.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className="inline-flex items-center justify-between gap-2 rounded-2xl px-5 py-4 text-base font-bold text-white transition hover:opacity-90"
                  style={{ backgroundColor: p.color }}
                >
                  <span>{p.label}</span>
                  <ExternalLink className="w-4 h-4 opacity-80" />
                </a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
